import { DEFAULT_PHONE_COUNTRY_CODE } from '@helpers/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { z } from 'zod';

const baseSchema = (t: TFunction) => ({
  phoneNumber: z
    .string()
    .min(9, t('FORMS.MIN_CHARACTERS', { limit: '7' }))
    .max(19, t('FORMS.MAX_CHARACTERS')),
  phoneExt: z.string().min(1, t('FORMS.REQUIRED')),
  email: z.string().email(t('FORMS.INVALID_EMAIL')).optional().or(z.literal('')),
  profiles: z.array(z.string()).min(1, t('FORMS.REQUIRED')),
  assignedVehicles: z.array(z.string()).optional(),
  labels: z.array(z.string()).optional(),
  costCenter: z.string().min(1, t('FORMS.REQUIRED')),
});

// Shema when allowEmptyNames = true
const schemaAllowEmpty = (t: TFunction) =>
  z.object({
    ...baseSchema(t),
    firstName: z
      .string()
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 }))
      .trim()
      .optional()
      .or(z.literal('')),
    lastName: z
      .string()
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 }))
      .optional()
      .or(z.literal('')),
    allowEmptyNames: z.literal(true),
  });

// Shema when allowEmptyNames = false
const schemaRequireNames = (t: TFunction) =>
  z.object({
    ...baseSchema(t),
    firstName: z
      .string()
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
    lastName: z
      .string()
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
    allowEmptyNames: z.literal(false).default(false),
  });

// Final schema – union of two schemas
const editEmployeeFormSchema = (t: TFunction) =>
  z.union([schemaAllowEmpty(t), schemaRequireNames(t)]);

export const defaultEditEmployeesValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  phoneExt: DEFAULT_PHONE_COUNTRY_CODE,
  email: '',
  labels: [],
  assignedVehicles: [],
  profiles: [],
  costCenter: '',
  allowEmptyNames: false,
};

export type EditEmployeeForm = z.infer<ReturnType<typeof editEmployeeFormSchema>>;

export const useEditEmployeeDrawerForm = () => {
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(editEmployeeFormSchema(t)),
    defaultValues: defaultEditEmployeesValues,
    mode: 'onBlur',
  });

  return { form };
};
